import Dialog from './Dialog';
import React from 'react';
import { SUPPORT_EMAIL } from '../constants';

const LAST_UPDATED = '1 October 2026';

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h3 className="text-white font-semibold mb-1">{title}</h3>
    <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const PrivacyPolicy = ({ onClose }) => (
  <Dialog onClose={onClose} label="Privacy Policy" className="fixed inset-0 z-modal bg-black/80 backdrop-blur-md flex items-center justify-center p-4 pointer-events-auto">
    <div
      className="max-w-lg w-full bg-slate-900 border border-white/10 rounded-3xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
    >
      <h2 className="text-2xl font-bold text-white mb-1">Privacy Policy</h2>
      <p className="text-gray-500 text-xs mb-5">Last updated: {LAST_UPDATED}</p>

      <Section title="Who we are">
        <p>TapTap is operated by TapTap Regulation Ltd (company number 16428806), and is a
          wellness app for relaxation, focus and self-regulation. It is not a medical device
          and does not diagnose or treat any condition.</p>
      </Section>

      <Section title="What we collect">
        <p>Your email address (passwords are handled securely by Google Firebase
          Authentication — we never see or store them), your in-app preferences, and
          aggregate usage statistics such as total taps, sessions, active time and timing
          information. If you use the optional routine check-in, we store counts of completed
          check-ins and how many times you reported feeling calmer. This is emotional
          wellbeing information, which we treat as health-related data — but we only ever
          store aggregate counts, never a diary of individual answers.</p>
        <p>We do not store a step-by-step history of each individual tap.</p>
        <p>We store your account identifier, trial eligibility and subscription status. For
          web subscriptions, Stripe processes payment details; we store Stripe customer and
          subscription identifiers, but do not see or store your full card number. Apple processes iOS subscriptions; we store Apple transaction identifiers, product, expiry and renewal status, and a random identifier linking the purchase to your TapTap account.</p>
        <p>If you previously joined TapTap Pro early access on our website, Tally stores the adult email
          address you provide, the date of your request, your consent and limited campaign
          information from the link you followed.</p>
      </Section>

      <Section title="How we use it">
        <p>To create and secure your account, sync your preferences across devices, and show
          your personal statistics, manage Pro access and prevent repeated trial use or billing abuse.
          We do not sell your data, show ads, or use third-party advertising trackers.</p>
        <p>We use a Pro early-access address only to tell you when TapTap Pro is available. We
          never send your email address to our website analytics provider.</p>
      </Section>

      <Section title="Motion and sensory preferences">
        <p>Your device’s Reduce Motion preference is used on your device to adjust animations. TapTap does not save this preference to your account. Choices you make in TapTap’s Settings, such as Sound, Haptics and Show Progress, are saved with your other in-app preferences.</p>
      </Section>

      <Section title="Website analytics">
        <p>We use Plausible Analytics to understand aggregate website visits, installation
          guide views, clicks to open TapTap and completed Pro notification signups. It does
          not use cookies or persistent identifiers. Campaign parameters may be used to
          compare aggregate conversion sources.</p>
      </Section>

      <Section title="Who processes it">
        <p>Google Firebase (Authentication and Cloud Firestore) stores account and settings
          information on our behalf. Stripe processes web subscription payments. Apple processes App Store subscription payments. Tally stores
          historical Pro early-access form responses. Plausible Analytics processes aggregate
          public-website usage information.</p>
      </Section>

      <Section title="Legal basis (UK GDPR)">
        <p>We process your data to provide the service you sign up for (performance of a
          contract) and, where relevant, with your consent.</p>
      </Section>

      <Section title="Keeping and deleting your data">
        <p>Settings → Delete Account (or Delete account on the email-verification screen) cancels any Stripe subscription and removes your sign-in
          account, saved settings, statistics, Apple purchase-to-account link and Pro entitlement. Apple subscriptions are not cancelled by deleting your TapTap account: cancel them separately in your Apple subscription settings. If billing cancellation fails,
          the account is retained so you can retry.</p>
        <p>To enforce one trial per verified email, we retain a hashed version of that email,
          the account identifier and the date the trial was used after account deletion. A hash
          is not the plain email address, but is still treated as personal data.</p>
        <p>We also retain limited account-deletion markers, request-limit counters and processed
          billing-event records to prevent abuse and stop delayed events restoring a deleted
          account. These records are not your saved settings or session history.</p>
        <p>Stripe and Apple retain payment records under their own retention requirements and privacy policies.</p>
        <p>We keep a Pro early-access address until the launch invitation has been sent,
          then delete it within 90 days. We review the list at least annually and you can ask
          us to remove your address sooner.</p>
      </Section>

      <Section title="Your rights">
        <p>You have the right to access, correct, or delete your data, and to complain to the
          ICO (ico.org.uk). To exercise these rights, contact us below.</p>
      </Section>

      <Section title="Children">
        <p>Kids mode is a child-friendly theme — calming animals and gentle sounds —
          designed for children to use under the supervision of a parent or guardian.</p>
        <p>Accounts are created and managed by an adult. We do not knowingly let children
          set up their own accounts, and we do not collect personal information directly
          from a child. Preferences and aggregate activity recorded during supervised use are
          stored under the adult’s account, as described above.</p>
      </Section>

      <Section title="Contact">
        <p>Questions or requests: <span className="text-indigo-300">{SUPPORT_EMAIL}</span></p>
      </Section>

      <button
        onClick={onClose}
        className="w-full mt-2 py-3 text-white font-bold rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all transform active:scale-95 shadow-lg shadow-purple-500/25"
      >
        Close
      </button>
    </div>
  </Dialog>
);

export default PrivacyPolicy;
